// install (please make sure versions match peerDependencies)
// yarn add @nivo/core @nivo/bar
import { ResponsiveBar } from '@nivo/bar';
import { useTheme } from '@nivo/core';
import { AxisTickProps } from '@nivo/axes';

interface IDataItem {
  [key: string]: string | number;
}

const exampleData: IDataItem[] = [
  {
    'category': 'Your score/550',
    'max score': 707,
    'max scoreColor': 'hsl(186, 70%, 50%)',
  },
  {
    'category': '', // divider line
    'max score': 0,
    'max scoreColor': 'hsl(186, 70%, 50%)',
  },
  {
    'category': 'Industry one/300 - 350',
    'max score': 174,
    'max scoreColor': 'hsl(257, 70%, 50%)',
    'score': 510,
    'scoreColor': 'hsl(14, 70%, 50%)',
  },
  {
    'category': 'Industry two/350 - 400',
    'max score': 450,
    'max scoreColor': 'hsl(145, 70%, 50%)',
    'score': 250,
    'scoreColor': 'hsl(281, 70%, 50%)',
  },
  {
    'category': 'Industry three/300 - 350',
    'max score': 106,
    'max scoreColor': 'hsl(226, 70%, 50%)',
    'score': 520,
    'scoreColor': 'hsl(281, 70%, 50%)',
  },
];

const CustomTick = (tick: AxisTickProps<string>) => {
  const theme = useTheme();
  const texts = tick.value.split('/');

  return (
    <g transform={`translate(${tick.x},${tick.y + 22})`}>
      <text
        textAnchor="middle"
        dominantBaseline="middle"
        style={{
          ...theme.axis.ticks.text,
          fill: '#333',
          fontSize: 16,
        }}
      >
        {texts[0]}
      </text>
      {texts[1] && (
        <text
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            ...theme.axis.ticks.text,
            fill: '#333',
            fontSize: 20,
            transform: 'translateY(20px)',
          }}
        >
          {texts[1]}
        </text>
      )}
    </g>
  );
};

// make sure parent container have a defined height when using
// responsive component, otherwise height will be 0 and
// no chart will be rendered.
// website examples showcase many properties,
// you'll often use just a few of them.
const Barchart = ({ data }: { data?: IDataItem[]}) => {
  console.log(exampleData);
  return (
    <ResponsiveBar
      data={data || exampleData}
      keys={[
        'max score',
        'score',
      ]}
      indexBy="category"
      margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
      padding={0.3}
      valueScale={{ type: 'linear' }}
      indexScale={{ type: 'band', round: true }}
      colors={{ scheme: 'nivo' }}
      defs={[
        {
          id: 'dots',
          type: 'patternDots',
          background: 'inherit',
          color: '#38bcb2',
          size: 4,
          padding: 1,
          stagger: true
        },
        {
          id: 'lines',
          type: 'patternLines',
          background: 'inherit',
          color: '#eed312',
          rotation: -45,
          lineWidth: 6,
          spacing: 10
        }
      ]}
      fill={[
        {
          match: {
            id: 'score'
          },
          id: 'dots'
        },
        {
          match: {
            id: 'max score'
          },
          id: 'lines'
        }
      ]}
      borderColor={{
        from: 'color',
        modifiers: [
          [
            'darker',
            1.6
          ]
        ]
      }}
      axisTop={null}
      axisRight={null}
      maxValue={1000}
      gridYValues={[0, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000]}
      enableGridX={true}
      axisBottom={{
        renderTick: CustomTick,
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: 'category',
        legendPosition: 'middle',
        legendOffset: 32
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: 'score',
        legendPosition: 'middle',
        legendOffset: -40
      }}
      labelSkipWidth={12}
      labelSkipHeight={12}
      labelTextColor={{
        from: 'color',
        modifiers: [
          [
            'darker',
            1.6
          ]
        ]
      }}
      legends={[
        {
          dataFrom: 'keys',
          anchor: 'bottom-right',
          direction: 'column',
          justify: false,
          translateX: 120,
          translateY: 0,
          itemsSpacing: 2,
          itemWidth: 100,
          itemHeight: 20,
          itemDirection: 'left-to-right',
          itemOpacity: 0.85,
          symbolSize: 20,
          effects: [
            {
              on: 'hover',
              style: {
                itemOpacity: 1
              }
            }
          ]
        }
      ]}
      role="application"
      ariaLabel="Nivo bar chart demo"
      barAriaLabel={(e) => `${e.id}: ${e.formattedValue} in category: ${e.indexValue}`}
    />
  );
};

export default Barchart;
